import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Chip,
  Divider,
  alpha,
  Tooltip
} from "@mui/material";
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { sessionManager } from "../context/sessionManager";
import TimerIcon from '@mui/icons-material/Timer';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import ProfileIcon from "@mui/icons-material/AccountCircle";
import RefreshIcon from '@mui/icons-material/Refresh';
import SecurityIcon from '@mui/icons-material/Security';
// import SettingsIcon from '@mui/icons-material/Settings';
import { useTheme } from "@mui/material/styles";
import { WorkspacePremium } from "@mui/icons-material";
import { useReactDevTools } from "../context/ReactDevTools";

const SessionIndicator = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isDarkMode = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const [sessionTime, setSessionTime] = useState("3h 0m");
  const [userData, setUserData] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showWarning, setShowWarning] = useState(false);
  const timerRef = useRef(null);
  const { disableSecretMode } = useReactDevTools() || {};

  const open = Boolean(anchorEl);

  // Theme colors for consistent styling
  const colors = {
    light: {
      primary: theme.palette.primary.main, // #2563EB
      primaryLight: theme.palette.primary.light, // #3B82F6
      primaryDark: theme.palette.primary.dark, // #1D4ED8
      secondary: theme.palette.secondary.main, // #8B5CF6
      secondaryLight: theme.palette.secondary.light, // #A78BFA
      textPrimary: theme.palette.text.primary, // #0F172A
      textSecondary: theme.palette.text.secondary, // #334155
      background: theme.palette.mode === 'light'
        ? 'rgba(255, 255, 255, 0.3)'
        : 'rgba(30, 41, 59, 0.3)',
      border: isDarkMode
        ? 'rgba(148, 163, 184, 0.1)'
        : 'rgba(99, 102, 241, 0.2)',
      chip: {
        background: 'rgba(37, 99, 235, 0.1)',
        border: 'rgba(37, 99, 235, 0.3)',
        text: '#2563EB'
      }
    },
    dark: {
      primary: theme.palette.primary.main, // #60A5FA
      primaryLight: theme.palette.primary.light, // #93C5FD
      primaryDark: theme.palette.primary.dark, // #3B82F6
      secondary: theme.palette.secondary.main, // #A78BFA
      secondaryLight: theme.palette.secondary.light, // #C4B5FD
      textPrimary: theme.palette.text.primary, // #FFFFFF
      textSecondary: theme.palette.text.secondary, // #E2E8F0
      background: theme.palette.mode === 'dark'
        ? 'rgba(30, 41, 59, 0.7)'
        : 'rgba(255, 255, 255, 0.92)',
      border: isDarkMode
        ? 'rgba(148, 163, 184, 0.1)'
        : 'rgba(99, 102, 241, 0.2)',
      chip: {
        background: 'rgba(96, 165, 250, 0.15)',
        border: 'rgba(96, 165, 250, 0.3)',
        text: '#60A5FA'
      }
    }
  };

  const currentColors = isDarkMode ? colors.dark : colors.light;

  const handleAutoLogout = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    disableSecretMode();
    sessionManager.clearSession();
    localStorage.removeItem("appAuth");
    navigate('/login');
  }, [navigate, disableSecretMode]);

  useEffect(() => {
    // Get user data from session
    const user = sessionManager.getUser();
    setUserData(user);

    const updateSessionTimer = () => {
      // Use only the methods that exist in your sessionManager
      const timeLeft = sessionManager.getFormattedRemainingTime ?
        sessionManager.getFormattedRemainingTime() : "3h 0m";

      setSessionTime(timeLeft);

      // Calculate session percentage from time string (for 3 hours = 180 minutes)
      const sessionPercentage = calculatePercentageFromTime(timeLeft);
      setShowWarning(sessionPercentage < 20);

      // If expired, logout automatically
      if (timeLeft === 'Expired' || sessionPercentage <= 0) {
        handleAutoLogout();
      }
    };

    updateSessionTimer();

    // Update every second for smooth countdown
    timerRef.current = setInterval(updateSessionTimer, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [handleAutoLogout]);

  // Helper function to calculate percentage from time string
  const calculatePercentageFromTime = (timeStr) => {
    if (timeStr === 'Expired') return 0;

    try {
      // Parse time like "3h 0m" or "2h 45m"
      const hoursMatch = timeStr.match(/(\d+)h/);
      const minutesMatch = timeStr.match(/(\d+)m/);

      let totalMinutes = 0;
      if (hoursMatch) totalMinutes += parseInt(hoursMatch[1]) * 60;
      if (minutesMatch) totalMinutes += parseInt(minutesMatch[1]);

      // Assuming 3 hours (180 minutes) session
      const sessionDuration = 180; // 3 hours in minutes
      return Math.round((totalMinutes / sessionDuration) * 100);
    } catch (error) {
      return 100; // Default to 100% if parsing fails
    }
  };

  const handleProfile = () => {
    navigate("/Profile");
    handleMenuClose();
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    disableSecretMode();
    sessionManager.clearSession();
    localStorage.removeItem("appAuth");
    navigate('/login');
  };

  const refreshSession = () => {
    // Use method checking to avoid errors
    if (sessionManager.refreshSession && sessionManager.refreshSession()) {
      const timeLeft = sessionManager.getFormattedRemainingTime ?
        sessionManager.getFormattedRemainingTime() : "3h 0m";
      setSessionTime(timeLeft);
      setShowWarning(false);
      handleMenuClose();
    } else if (sessionManager.refreshSession) {
      // Just refresh if no return value
      sessionManager.refreshSession();
      const timeLeft = sessionManager.getFormattedRemainingTime ?
        sessionManager.getFormattedRemainingTime() : "3h 0m";
      setSessionTime(timeLeft);
      setShowWarning(false);
      handleMenuClose();
    }
  };

  const getTimerColor = () => {
    const percentage = calculatePercentageFromTime(sessionTime);
    if (percentage < 10) return '#EF4444'; // Red for critical
    if (percentage < 30) return '#F59E0B'; // Orange for warning
    return '#10B981'; // Green for normal
  };

  const getTimeForDisplay = () => {
    if (sessionTime === 'Expired') return 'Expired';
    return sessionTime;
  };

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      position: 'relative'
    }}>
      {/* Session Timer - Side by side layout */}
      <Tooltip
        title={
          <Box>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
              Session expires in: {sessionTime}
            </Typography>
            {showWarning && (
              <Typography variant="caption" sx={{ color: '#F59E0B', display: 'block' }}>
                Session expiring soon
              </Typography>
            )}
          </Box>
        }
        arrow
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            padding: '6px 12px',
            borderRadius: '12px',
            background: isDark
              ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(15, 23, 42, 0.8))'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.7), rgba(248, 250, 252, 0.9))',
            backdropFilter: 'blur(10px)',
            border: `1px solid ${alpha(isDark ? '#475569' : '#e2e8f0', 0.3)}`,
            transition: 'all 0.2s ease',
            cursor: 'pointer',
            '&:hover': {
              background: isDark
                ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8), rgba(15, 23, 42, 0.9))'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 1))',
              borderColor: alpha(getTimerColor(), 0.4),
              transform: 'translateY(-1px)',
              boxShadow: `0 4px 12px ${alpha(getTimerColor(), 0.1)}`,
            }
          }}
        >
          {/* Timer Icon */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative'
          }}>
            <TimerIcon
              sx={{
                fontSize: 18,
                color: getTimerColor(),
                animation: showWarning ? 'pulse 2s infinite' : 'none',
                '@keyframes pulse': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.6 },
                  '100%': { opacity: 1 },
                }
              }}
            />

            {/* Warning Badge */}
            {showWarning && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: '#EF4444',
                  border: `2px solid ${isDark ? '#0F172A' : '#FFFFFF'}`,
                  animation: 'blink 1s infinite',
                  '@keyframes blink': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.5 },
                  }
                }}
              />
            )}
          </Box>

          {/* Time Text - Side by side with icon */}
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: getTimerColor(),
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              minWidth: '45px',
              textAlign: 'center'
            }}
          >
            {getTimeForDisplay()}
          </Typography>
        </Box>
      </Tooltip>

      {/* User Profile */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        padding: '6px 12px',
        borderRadius: '12px',
        background: isDark
          ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(15, 23, 42, 0.8))'
          : 'linear-gradient(135deg, rgba(255, 255, 255, 0.7), rgba(248, 250, 252, 0.9))',
        backdropFilter: 'blur(10px)',
        border: `1px solid ${alpha(isDark ? '#475569' : '#e2e8f0', 0.3)}`,
        transition: 'all 0.2s ease',
        '&:hover': {
          background: isDark
            ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8), rgba(15, 23, 42, 0.9))'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 1))',
          borderColor: alpha(isDark ? '#60A5FA' : '#3B82F6', 0.4),
          transform: 'translateY(-1px)',
          boxShadow: `0 4px 12px ${alpha(isDark ? '#60A5FA' : '#3B82F6', 0.1)}`,
        }
      }}>
        {userData && (
          <>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              mr: 1
            }}>
              <Typography
                variant="caption"
                sx={{
                  color: isDark ? '#E2E8F0' : '#1E293B',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  lineHeight: 1.2
                }}
              >
                {userData.shopName || userData.name}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: isDark ? '#94A3B8' : '#64748B',
                  fontSize: '0.7rem',
                  lineHeight: 1.2
                }}
              >
                Admin
              </Typography>
            </Box>

            <Tooltip title="Account Settings">
              <IconButton
                onClick={handleMenuClick}
                size="small"
                sx={{
                  color: isDark ? '#E2E8F0' : '#1E293B',
                  background: isDark
                    ? 'rgba(139, 92, 246, 0.1)'
                    : 'rgba(139, 92, 246, 0.08)',
                  border: `1px solid ${alpha('#8B5CF6', 0.2)}`,
                  '&:hover': {
                    background: isDark
                      ? 'rgba(139, 92, 246, 0.2)'
                      : 'rgba(139, 92, 246, 0.15)',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                {userData.avatar ? (
                  <Avatar
                    src={userData.avatar}
                    sx={{ width: 28, height: 28 }}
                  />
                ) : (
                  <AccountCircleIcon />
                )}
              </IconButton>
            </Tooltip>
          </>
        )}

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              mt: 1.5,
              borderRadius: '12px',
              background: isDark
                ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95), rgba(15, 23, 42, 0.98))'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.98))',
              backdropFilter: 'blur(20px)',
              border: `1px solid ${alpha(isDark ? '#475569' : '#e2e8f0', 0.3)}`,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
              minWidth: 240,
            }
          }}
        >
          {userData && (
            <Box sx={{ p: 2, pb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    background: 'linear-gradient(135deg, #8B5CF6, #A78BFA)',
                    border: `2px solid ${alpha('#8B5CF6', 0.3)}`,
                  }}
                >
                  {userData.name?.charAt(0) || 'U'}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: isDark ? '#F1F5F9' : '#1E293B' }}>
                    {userData.shopName || userData.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
                    {userData.email}
                  </Typography>
                </Box>
              </Box>

              <Chip
                label="Administrator"
                size="small"
                icon={<SecurityIcon sx={{ fontSize: 14 }} />}
                sx={{
                  mt: 1,
                  background: alpha('#10B981', 0.1),
                  color: '#10B981',
                  border: `1px solid ${alpha('#10B981', 0.2)}`,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                }}
              />
            </Box>
          )}

          <Divider sx={{ borderColor: alpha(isDark ? '#475569' : '#e2e8f0', 0.3) }} />

          {/* Session Status in Menu */}
          {/* <Box sx={{ p: 2, pb: 1 }}>
            <Typography variant="caption" sx={{ 
              color: isDark ? '#94A3B8' : '#64748B',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              mb: 1,
              display: 'block'
            }}>
              Session Status
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <TimerIcon sx={{ fontSize: 16, color: getTimerColor() }} />
              <Typography variant="body2" sx={{ fontWeight: 600, color: getTimerColor() }}>
                {sessionTime} remaining
              </Typography>
            </Box>
          </Box> */}

          <Divider sx={{ borderColor: alpha(isDark ? '#475569' : '#e2e8f0', 0.3) }} />

          {/* Menu Items */}
          <MenuItem
            onClick={handleProfile}
            sx={{
              py: 1.2,
              px: 2,
              borderRadius: '12px',
              mx: 1,
              my: 0.5,
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                backgroundColor: isDarkMode
                  ? "rgba(96, 165, 250, 0.15)"
                  : "rgba(37, 99, 235, 0.1)",
              },
            }}
          >
            <ProfileIcon
              sx={{
                mr: 1.5,
                fontSize: 22,
                color: currentColors.primary,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: currentColors.textPrimary,
              }}
            >
              My Profile
            </Typography>
          </MenuItem>


          {/* Menu Items */}
          <MenuItem
            onClick={() => {
              handleMenuClose();
              navigate('/SubscriptionPage');
            }}
            sx={{
              py: 1.2,
              px: 2,
              borderRadius: '12px',
              mx: 1,
              my: 0.5,
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                backgroundColor: isDarkMode
                  ? "rgba(96, 165, 250, 0.15)"
                  : "rgba(37, 99, 235, 0.1)",
              },
            }}
          >
            <WorkspacePremium
              sx={{
                mr: 1.5,
                fontSize: 22,
                color: currentColors.primary,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: currentColors.textPrimary,
              }}
            >
              Subscription Plans
            </Typography>
          </MenuItem>

          <Divider sx={{ borderColor: alpha(isDark ? '#475569' : '#e2e8f0', 0.3) }} />
          <MenuItem
            onClick={refreshSession}
            sx={{
              py: 1.5,
              px: 2,
              '&:hover': {
                background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
              }
            }}
          >
            <RefreshIcon sx={{
              mr: 2,
              fontSize: 20,
              color: '#3B82F6'
            }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Refresh Session
              </Typography>
              <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
                Extend for 3 more hours
              </Typography>
            </Box>
          </MenuItem>

          {/* <MenuItem 
            onClick={() => {
              handleMenuClose();
              navigate('/settings');
            }}
            sx={{
              py: 1.5,
              px: 2,
              '&:hover': {
                background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
              }
            }}
          >
            <SettingsIcon sx={{ 
              mr: 2, 
              fontSize: 20,
              color: '#8B5CF6'
            }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Settings
              </Typography>
              <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
                Account preferences
              </Typography>
            </Box>
          </MenuItem> */}



          <MenuItem
            onClick={handleLogout}
            sx={{
              py: 1.5,
              px: 2,
              '&:hover': {
                background: alpha('#EF4444', 0.1),
              }
            }}
          >
            <LogoutIcon sx={{
              mr: 2,
              fontSize: 20,
              color: '#EF4444'
            }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#EF4444' }}>
                Logout
              </Typography>
              <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
                End current session
              </Typography>
            </Box>
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

export default SessionIndicator;